import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { book } from "../../components/apipath";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import Address from "../registration/component/address";

function BookRequestForm() {
    const { token, mem_id, mem_status, authorized } = useAuth();
    const navigate = useNavigate();

    const [checkingEligibility, setCheckingEligibility] = useState(true);
    const [eligible, setEligible] = useState(false);

    const [formData, setFormData] = useState({
        mem_id: mem_id,
        studentName: "",
        account: "",
        core_subjects: [],
        specialty: "",
        requested_date: "",
        memo: "",
        address: "",
        status: "審核中",
        delivery_method: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const checkAccess = async () => {
            if (!authorized || String(mem_status) !== "1") {
                await Swal.fire({
                    icon: "warning",
                    title: "學員專屬功能",
                    text: "此頁面僅限學員使用，請先完成購買並登入",
                    confirmButtonText: "了解",
                    confirmButtonColor: "#224040",
                });
                navigate("/member/memberlogin");
                return;
            }

            try {
                const authData = JSON.parse(localStorage.getItem("auth"));
                if (authData && authData.chinese_name) {
                    setFormData((prevData) => ({
                        ...prevData,
                        studentName: authData.chinese_name,
                        account: authData.account || "",
                    }));
                }
            } catch (error) {
                console.error("無法解析 auth 資料", error);
            }

            try {
                const res = await axios.get(`${book}check-eligibility`, {
                    headers: { token },
                    params: { mem_id },
                });

                if (res.data?.eligible) {
                    setEligible(true);
                } else {
                    await Swal.fire({
                        icon: "warning",
                        title: "目前無法使用此表單",
                        text:
                            res.data?.reason ||
                            "需有加購買書本項目，才需填寫課本領取預約表。",
                        confirmButtonText: "了解",
                        confirmButtonColor: "#224040",
                    });
                    navigate("/member");
                    return;
                }
            } catch (error) {
                console.error("資格檢查失敗:", error);
                await Swal.fire({
                    icon: "error",
                    title: "資格檢查失敗",
                    text: error.response?.data?.message || "系統忙碌中，請稍後再試",
                    confirmButtonText: "了解",
                });
                navigate("/member");
                return;
            } finally {
                setCheckingEligibility(false);
            }
        };

        checkAccess();
    }, [authorized, mem_status, mem_id, token, navigate]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: type === "file" ? files[0] : value,
        }));
    };

    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;

        setFormData((prevData) => {
            let updatedSubjects;

            if (checked) {
                updatedSubjects = [...new Set([...prevData.core_subjects, value])];
            } else {
                updatedSubjects = prevData.core_subjects.filter(
                    (subject) => subject !== value
                );
            }

            return {
                ...prevData,
                core_subjects: updatedSubjects,
            };
        });
    };

    const handleRadioChange = (e) => {
        const { value } = e.target;

        setFormData((prevData) => {
            const updatedSubjects = prevData.core_subjects.filter(
                (sub) => sub !== prevData.specialty
            );

            return {
                ...prevData,
                specialty: value,
                core_subjects: [...updatedSubjects, value],
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const dataToSend = {
                mem_id: mem_id || "",
                student_name: formData.studentName,
                account: formData.account,
                core_subject: formData.core_subjects.join(","),
                requested_date: formData.requested_date || null,
                memo: formData.memo,
                address: formData.address,
                delivery_method: formData.delivery_method,
                status: "審核中",
            };

            const response = await axios.post(`${book}`, dataToSend, {
                headers: { token },
            });

            if (response.status === 200) {
                await Swal.fire({
                    icon: "success",
                    title: "申請成功",
                    text: "請等待管理員審核！",
                    confirmButtonText: "確認",
                });
                navigate("/member");
            }
        } catch (error) {
            console.error("申請失敗:", error);
            Swal.fire({
                icon: "error",
                title: "提交失敗",
                text:
                    error.response?.data?.message ||
                    "提交失敗，請稍後再試。",
                confirmButtonText: "確認",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (checkingEligibility) {
        return (
            <div className="container mb100 mt100 text-center">
                <h4>資格確認中...</h4>
            </div>
        );
    }

    if (!eligible) return null;

    return (
        <div className="container mb100 mt100">
            <h2 className="font-weight-bold pb-5 text-center">
                Becker 全系列 Exam Review 教材 - 紙本課本 (textbook) 領取預約表
            </h2>
            <div className="card w-75 p-5 m-auto">
                <div className="card-body">
                    <h3 className="fw-bold">Becker Taiwan 課本領取預約公告</h3>

                    <div>
                        除紙本課本外，您亦可隨時透過 Becker 軟體線上閱覽電子書（Digital
                        Textbook）。電子書內容與紙本課本完全一致，且更新頻率通常更高。
                        <br />
                        <br />
                        <br />
                        填寫本「課本領取預約表」，即表示您確認將
                        <span className="fw-bold">立即領取</span>
                        所勾選之科目與版本。
                        <br />
                        我們在收到您的預約申請後，於庫存充足的情況下，將盡快安排您
                        <span className="fw-bold">現場領取或以快遞方式寄送。</span>
                        <br />
                        <br />
                        <br />
                        請特別留意以下事項：
                        <br />
                        <ol>
                            <li>
                                若您目前尚未準備修讀某一科目（例如尚未開始準備該科考試），請勿填寫本表。
                            </li>
                            <li>
                                表單一經填寫並送出，即視為您確認：
                                <ul>
                                    <li>領取所選科目之課本</li>
                                    <li>以一次完成領取或寄送為原則</li>
                                </ul>
                            </li>
                            <li>
                                恕無法將已填寫之科目拆開延後寄送，或僅先領取部分科目之課本。
                            </li>
                        </ol>
                        如遇課本庫存不足之情況，Becker Taiwan
                        團隊將另行與您聯繫，並協助協調適當的領取或寄送時間。
                        <br />
                        感謝您的理解與配合。
                        <br />
                        <div className="mt-5">
                            <hr />
                        </div>
                    </div>

                    {/* 下面其餘你的表單內容照舊 */}
                    <form onSubmit={handleSubmit}>
                        <div className="form-group mb-4">
                            <label>學員姓名</label>
                            <input
                                type="text"
                                className="form-control"
                                name="studentName"
                                value={formData.studentName}
                                readOnly
                            />
                        </div>

                        <div className="form-group mb-4">
                            <label>您的 Becker 軟體帳號（email 全址）*</label>
                            <input
                                className="form-control"
                                name="account"
                                value={formData.account}
                                onChange={handleChange}
                            />
                        </div>

                        <p>
                            若您曾經領取過 某科目課本(如:FAR、AUD、REG...)，希望再次領取，則不需填寫此表單，請改填寫{" "}
                            <Link to="../member/add">Becker 加購表單</Link>。
                        </p>

                        <div className="form-group mb-4">
                            <label className="mb-2">
                                預計領取課本科目（CPA / CMA / EA），請勾選接下來確定會備考的科目。(皆為最新版本)
                            </label>

                            <div className="mb-2 font-weight-bold">CPA core 科目：</div>
                            <div className="checkbox-group mb-3">
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="FAR"
                                        checked={formData.core_subjects.includes("FAR")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    FAR v1.5
                                </label>
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="REG"
                                        checked={formData.core_subjects.includes("REG")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    REG v1.4
                                </label>
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="AUD"
                                        checked={formData.core_subjects.includes("AUD")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    AUD v1.3
                                </label>
                            </div>

                            <div className="mb-2 font-weight-bold">CMA 課本：</div>
                            <div className="checkbox-group mb-3">
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="CMA Part 1"
                                        checked={formData.core_subjects.includes("CMA Part 1")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    CMA Part 1 v2.0
                                </label>
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="CMA Part 2"
                                        checked={formData.core_subjects.includes("CMA Part 2")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    CMA Part 2 v2.0
                                </label>
                            </div>

                            <div className="mb-2 font-weight-bold">EA 課本：</div>
                            <div className="checkbox-group">
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="EA Part 1"
                                        checked={formData.core_subjects.includes("EA Part 1")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    EA Part 1 v1.2
                                </label>
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="EA Part 2"
                                        checked={formData.core_subjects.includes("EA Part 2")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    EA Part 2 v1.2
                                </label>
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        value="EA Part 3"
                                        checked={formData.core_subjects.includes("EA Part 3")}
                                        onChange={handleCheckboxChange}
                                    />{" "}
                                    EA Part 3 v1.2
                                </label>
                            </div>
                        </div>

                        <div className="form-group mb-4">
                            <label className="mb-2">預計領取專科 (discipline) 課本</label>
                            <div className="radio-group mt-2">
                                <label className="form-check">
                                    <input
                                        type="radio"
                                        name="specialty"
                                        value="TCP"
                                        checked={formData.specialty === "TCP"}
                                        onChange={handleRadioChange}
                                    />{" "}
                                    TCP v1.4
                                </label>
                                <label className="form-check">
                                    <input
                                        type="radio"
                                        name="specialty"
                                        value="BAR"
                                        checked={formData.specialty === "BAR"}
                                        onChange={handleRadioChange}
                                    />{" "}
                                    BAR v1.3
                                </label>
                                <label className="form-check">
                                    <input
                                        type="radio"
                                        name="specialty"
                                        value="ISC"
                                        checked={formData.specialty === "ISC"}
                                        onChange={handleRadioChange}
                                    />{" "}
                                    ISC v1.2
                                </label>
                            </div>
                        </div>

                        <div className="form-group mb-4">
                            <label className="mb-2">領書方式</label>
                            <br />
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id="delivery_post"
                                    name="delivery_method"
                                    value="post"
                                    checked={formData.delivery_method === "post"}
                                    onChange={handleChange}
                                />
                                <label className="form-check-label" htmlFor="delivery_post">
                                    郵寄
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id="delivery_pickup"
                                    name="delivery_method"
                                    value="pickup"
                                    checked={formData.delivery_method === "pickup"}
                                    onChange={handleChange}
                                />
                                <label className="form-check-label" htmlFor="delivery_pickup">
                                    現場自取
                                </label>
                            </div>
                        </div>

                        {formData.delivery_method === "post" && (
                            <div className="form-group mb-4">
                                <label htmlFor="address" className="mb-2">
                                    發票/紙本教科書郵寄地址
                                </label>
                                <Address
                                    onAddressChange={(combinedAddress) =>
                                        setFormData({ ...formData, address: combinedAddress })
                                    }
                                />
                            </div>
                        )}

                        {formData.delivery_method === "pickup" && (
                            <div className="form-group mb-4">
                                <label>欲領取時間（請選擇三日後的日期）</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="requested_date"
                                    value={formData.requested_date}
                                    onChange={handleChange}
                                    min={new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
                                        .toISOString()
                                        .split("T")[0]}
                                    required
                                />
                            </div>
                        )}

                        <div className="form-group mb-4">
                            <label>備註</label>
                            若有匯款，請填寫匯款人姓名或帳號末五碼。任何其他課本相關疑問或建議，請在此留言。
                            <textarea
                                className="form-control"
                                name="memo"
                                rows="3"
                                value={formData.memo}
                                onChange={handleChange}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "提交中..." : "提交申請"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default BookRequestForm;